import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaClock, FaEye, FaCalendar } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { blogAPI } from '../utils/api';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 120px var(--space-md) 80px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  color: var(--primary-600);
  text-decoration: none;
  margin-bottom: var(--space-xl);
  
  &:hover {
    color: var(--primary-700);
  }
`;

const PostHeader = styled.header`
  text-align: center;
  margin-bottom: var(--space-3xl);
`;

const PostCategory = styled.span`
  background: var(--primary-100);
  color: var(--primary-700);
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-md);
  font-weight: 500;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PostTitle = styled.h1`
  margin: var(--space-lg) 0;
  font-size: 2.5rem;
  line-height: 1.2;
  color: var(--secondary-900);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PostMeta = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--space-xl);
  color: var(--secondary-600);
  font-size: 0.875rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: var(--space-sm);
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-xs);
`;

const PostContent = styled.div`
  line-height: 1.8;
  font-size: 1.125rem;
  color: var(--secondary-700);
  max-width: 100%;

  /* Headings - Clear hierarchy */
  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-family-heading);
    font-weight: 600;
    line-height: 1.3;
    color: var(--secondary-900);
    margin-top: var(--space-2xl);
    margin-bottom: var(--space-lg);
  }

  h1 {
    font-size: 2.25rem;
    margin-top: 0;
    border-bottom: 2px solid var(--secondary-200);
    padding-bottom: var(--space-md);
  }

  h2 {
    font-size: 1.875rem;
    margin-top: var(--space-2xl);
  }

  h3 {
    font-size: 1.5rem;
    margin-top: var(--space-xl);
  }

  h4 {
    font-size: 1.25rem;
    margin-top: var(--space-xl);
  }

  h5 {
    font-size: 1.125rem;
    margin-top: var(--space-lg);
  }

  h6 {
    font-size: 1rem;
    margin-top: var(--space-lg);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  /* Paragraphs */
  p {
    margin-bottom: var(--space-lg);
    line-height: 1.8;
  }

  /* First paragraph after heading */
  h1 + p, h2 + p, h3 + p, h4 + p {
    margin-top: 0;
    font-size: 1.25rem;
    color: var(--secondary-600);
  }

  /* Lists - Better formatting */
  ul, ol {
    margin: var(--space-lg) 0;
    padding-left: var(--space-2xl);
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    margin-bottom: var(--space-sm);
    line-height: 1.8;
    padding-left: var(--space-sm);
  }

  /* Nested lists */
  ul ul, ol ol, ul ol, ol ul {
    margin-top: var(--space-sm);
    margin-bottom: var(--space-sm);
    padding-left: var(--space-xl);
  }

  ul ul {
    list-style-type: circle;
  }

  ul ul ul {
    list-style-type: square;
  }

  /* Blockquotes */
  blockquote {
    border-left: 4px solid var(--primary-600);
    margin: var(--space-xl) 0;
    padding: var(--space-lg) var(--space-xl);
    background: var(--secondary-50);
    font-style: italic;
    border-radius: 0 var(--radius-md) var(--radius-md) 0;
    color: var(--secondary-700);
    position: relative;
  }

  blockquote p {
    margin-bottom: var(--space-sm);
  }

  blockquote p:last-child {
    margin-bottom: 0;
  }

  /* Code blocks */
  code {
    background: var(--secondary-100);
    padding: 0.2em 0.4em;
    border-radius: var(--radius-sm);
    font-family: 'Courier New', 'Consolas', 'Monaco', monospace;
    font-size: 0.9em;
    color: var(--secondary-900);
  }

  pre {
    background: var(--secondary-100);
    padding: var(--space-lg);
    border-radius: var(--radius-md);
    overflow-x: auto;
    margin: var(--space-lg) 0;
    border: 1px solid var(--secondary-200);
  }

  pre code {
    background: transparent;
    padding: 0;
    font-size: 0.875rem;
    line-height: 1.6;
    display: block;
  }

  /* Text formatting */
  strong, b {
    color: var(--secondary-900);
    font-weight: 600;
  }

  em, i {
    color: var(--primary-600);
    font-style: italic;
  }

  u {
    text-decoration: underline;
    text-decoration-color: var(--primary-400);
  }

  s, strike {
    text-decoration: line-through;
    opacity: 0.7;
  }

  /* Links */
  a {
    color: var(--primary-600);
    text-decoration: underline;
    text-decoration-color: var(--primary-300);
    text-underline-offset: 2px;
    transition: all var(--transition-fast);
  }

  a:hover {
    color: var(--primary-700);
    text-decoration-color: var(--primary-500);
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: var(--space-xl) auto;
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-md);
  }

  /* Image alignment (from Quill) */
  img[style*="float: left"],
  .ql-align-left {
    float: left;
    margin-right: var(--space-lg);
    margin-bottom: var(--space-md);
  }

  img[style*="float: right"],
  .ql-align-right {
    float: right;
    margin-left: var(--space-lg);
    margin-bottom: var(--space-md);
  }

  .ql-align-center {
    text-align: center;
  }

  .ql-align-justify {
    text-align: justify;
  }

  /* Horizontal rules */
  hr {
    border: none;
    border-top: 2px solid var(--secondary-200);
    margin: var(--space-2xl) 0;
  }

  /* Tables (if Quill supports them) */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: var(--space-lg) 0;
    overflow-x: auto;
    display: block;
  }

  thead {
    background: var(--secondary-100);
  }

  th, td {
    padding: var(--space-md);
    border: 1px solid var(--secondary-200);
    text-align: left;
  }

  th {
    font-weight: 600;
    color: var(--secondary-900);
  }

  tr:nth-child(even) {
    background: var(--secondary-50);
  }

  /* Clear floats */
  &::after {
    content: "";
    display: table;
    clear: both;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    font-size: 1rem;
    
    h1 { font-size: 1.875rem; }
    h2 { font-size: 1.5rem; }
    h3 { font-size: 1.25rem; }
    h4 { font-size: 1.125rem; }

    ul, ol {
      padding-left: var(--space-xl);
    }

    img {
      margin: var(--space-lg) 0;
    }

    blockquote {
      padding: var(--space-md) var(--space-lg);
    }
  }
`;

const PostActions = styled.div`
  margin-top: var(--space-3xl);
  padding-top: var(--space-xl);
  border-top: 1px solid var(--secondary-200);
  text-align: center;
`;

const ShareButton = styled.button`
  background: var(--primary-600);
  color: white;
  padding: var(--space-sm) var(--space-lg);
  border: none;
  border-radius: var(--radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: background var(--transition-fast);

  &:hover {
    background: var(--primary-700);
  }
`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const author = 'Scrum Master & Agile Coach';

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await blogAPI.getBySlug(slug, i18n.language);
        setPost(response.data);
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug, i18n.language]);

  if (loading) {
    return (
      <Container>
        <p>{t('common.loading')}</p>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container>
        <p>{t('blog.postNotFound', 'Blog post not found')}</p>
        <BackLink to="/blog">
          <FaArrowLeft /> {t('blog.backToBlog', 'Back to Blog')}
        </BackLink>
      </Container>
    );
  }


  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert(t('blog.linkCopied', 'Link copied to clipboard!'));
    }
  };

  // BlogPosting structured data
  const blogPostSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.featuredImage ? `https://scrumnx.com${post.featuredImage}` : "https://scrumnx.com/og-image.jpg",
    "datePublished": post.publishedAt,
    "dateModified": post.updatedAt || post.publishedAt,
    "author": {
      "@type": "Person",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "ScrumNX",
      "logo": {
        "@type": "ImageObject",
        "url": "https://scrumnx.com/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://scrumnx.com/blog/${post.slug}`
    },
    "articleSection": post.category,
    "keywords": post.tags?.join(', ') || post.category,
    "timeRequired": `PT${post.readTime}M`
  };

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={post.tags?.join(', ') || 'Blog'}
        url={`https://scrumnx.com/blog/${post.slug}`}
        type="article"
        schema={blogPostSchema}
      />
      <Breadcrumbs
        items={[
          { label: t('common.home', 'Home'), path: '/', isCurrent: false },
          { label: t('common.blog', 'Blog'), path: '/blog', isCurrent: false },
          { label: post.title, path: `/blog/${post.slug}`, isCurrent: true }
        ]}
      />
      <Container>
        <BackLink to="/blog">
          <FaArrowLeft /> {t('blog.backToBlog', 'Back to Blog')}
        </BackLink>

        <PostHeader>
          <PostCategory>{post.category}</PostCategory>
          <PostTitle>{post.title}</PostTitle>
          <PostMeta>
            <MetaItem>
              <FaCalendar />
              <span>{new Date(post.publishedAt).toLocaleDateString(i18n.language)}</span>
            </MetaItem>
            <MetaItem>
              <FaClock />
              <span>{post.readTime} {t('blog.minRead', 'min read')}</span>
            </MetaItem>
            <MetaItem>
              <FaEye />
              <span>{post.views || 0} {t('blog.views', 'views')}</span>
            </MetaItem>
          </PostMeta>
        </PostHeader>

        <PostContent>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{post.content}</ReactMarkdown>
        </PostContent>

        <PostActions>
          <ShareButton onClick={handleShare}>
            {t('blog.sharePost', 'Share this post')}
          </ShareButton>
        </PostActions>
      </Container>
    </>
  );
};

export default BlogPostPage;